import React, { FC, Fragment } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import {
    Accordion,
    AccordionDetails,
    Box,
    Chip,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';
import { getHistoricalOrderTemplate, historicalOrderFileDownloadUrlTemplate } from 'apollo/templates/historical-order';
import { HistoricalOrder } from 'models/historical-order';
import { formatBytes, formatCurrency, formatDate } from 'utils/format';
import Loader from 'components/shared/loader/loader.component';
import InfoText from 'components/shared/info-text.component';
import { Helmet } from 'react-helmet-async';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import HistoricalOrderLines from './historical-order-lines.component';
import HistoricalOrderFiles from './historical-order-files.component';

/**
 * DD-3687: one archived order, read-only.
 *
 * Laid out as the live order details page is — a 9/3 grid of collapsible
 * sections with the same grey summary bar — so an operator moving between a
 * live order and an archived one is not relearning the page. What differs is
 * what is missing: no workflow, no actions, no editing. Nothing here is
 * derived either; the page renders exactly what was imported, including the
 * source system's own columns under "other details".
 *
 * Download links are minted one at a time rather than with the order: a signed
 * URL expires, and issuing one per attachment up front would hand out links
 * for files nobody opens.
 */

/** The live order details page's section header, so the two read as one app. */
const AccordionSummary = styled((props: AccordionSummaryProps) => <MuiAccordionSummary {...props} />)(() => ({
    background: '#f1f1f1',
    borderBottom: '1px #eee solid',
}));

const LabelledValue: FC<{ label: string; value?: string; md?: number }> = ({ label, value, md = 3 }) => (
    <Grid item md={md} sm={6} xs={12}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2">{value || '–'}</Typography>
    </Grid>
);

const HistoricalOrderDetail: FC = () => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { historicalOrderId } = useParams();
    const { showBoundary } = useDenthubErrorBoundary();

    const { data, loading } = useQuery<{ historicalOrder: HistoricalOrder }>(getHistoricalOrderTemplate, {
        variables: { id: historicalOrderId },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        onError: (error) => showBoundary(error),
    });

    const [fetchDownloadUrl] = useLazyQuery<{ historicalOrderFileDownloadUrl: string }>(
        historicalOrderFileDownloadUrlTemplate,
        { fetchPolicy: 'network-only', onError: (error) => showBoundary(error) },
    );

    const handleDownload = async (fileId: string) => {
        const { data: urlData } = await fetchDownloadUrl({
            variables: { historicalOrderId, fileId },
        });
        if (urlData?.historicalOrderFileDownloadUrl) {
            window.open(urlData.historicalOrderFileDownloadUrl, '_blank');
        }
    };

    if (loading && !data) {
        return <Loader />;
    }
    const historicalOrder = data?.historicalOrder;
    if (!historicalOrder) {
        return <InfoText text={translate('orders:HISTORICAL_ORDER_NOT_FOUND')} />;
    }

    const currency = historicalOrder.totals?.currency;
    const hasOtherDetails = !!historicalOrder.comments || historicalOrder.extraDetails.length > 0;

    return (
        <Fragment>
            {/* The live order details page has no heading either — the order number
                lives in the first section's summary and the tab carries the title. */}
            <Helmet>
                <title>{`${translate('orders:ORDER')} ${historicalOrder.orderNumber ?? ''} | Denthub`}</title>
            </Helmet>

            <Grid container spacing={2}>
                <Grid item xs={12} md={9}>
                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="historical-order-detail.order-accordion"
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="h4">
                                    {translate('orders:ORDER')}: {historicalOrder.orderNumber}
                                </Typography>
                                {/* Where the live page puts its inline markers. The card
                                    requires the order to read as historical and read-only. */}
                                <Tooltip title={translate('orders:HISTORICAL_READ_ONLY_EXPLANATION').toString()}>
                                    <Chip
                                        label={translate('orders:HISTORICAL_READ_ONLY')}
                                        size="small"
                                        data-test="historical-order-detail.read-only-chip"
                                    />
                                </Tooltip>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <LabelledValue
                                    label={translate('orders:ORDER_DATE')}
                                    value={
                                        historicalOrder.orderDate
                                            ? formatDate(new Date(historicalOrder.orderDate), 'date')
                                            : undefined
                                    }
                                />
                                <LabelledValue
                                    label={translate('orders:DELIVERY_DATE')}
                                    value={
                                        historicalOrder.deliveryDate
                                            ? formatDate(new Date(historicalOrder.deliveryDate), 'date')
                                            : undefined
                                    }
                                />
                                <LabelledValue
                                    label={translate('orders:CLINIC')}
                                    value={historicalOrder.customerName}
                                />
                                <LabelledValue label={translate('orders:LAB')} value={historicalOrder.labName} />
                                <LabelledValue
                                    label={translate('orders:DELIVERY_DESTINATION')}
                                    value={historicalOrder.deliveryDestinationName}
                                />
                                <LabelledValue
                                    label={translate('orders:PATIENT_REFERENCE')}
                                    value={historicalOrder.patientReference}
                                />
                                <LabelledValue label={translate('orders:STATUS')} value={historicalOrder.status} />
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="historical-order-detail.specification-accordion"
                        >
                            <Typography variant="h4">{translate('orders:PRICE_SPECIFICATION')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <HistoricalOrderLines
                                lines={historicalOrder.lines}
                                totalInclVat={historicalOrder.totals?.inclVat}
                                currency={currency}
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="historical-order-detail.messages-accordion"
                        >
                            <Typography variant="h4">{translate('orders:COMMENTS')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {historicalOrder.messages.length === 0 ? (
                                <InfoText text={translate('orders:NO_COMMENTS_AVAILABLE')} />
                            ) : (
                                historicalOrder.messages.map((message, messageIndex) => (
                                    <Box key={messageIndex} mb={2}>
                                        <Typography variant="caption" color="text.secondary">
                                            {message.sentAt ? formatDate(new Date(message.sentAt), 'datetime') : ''}
                                            {message.senderName ? ` · ${message.senderName}` : ''}
                                        </Typography>
                                        <Typography variant="body2">{message.text}</Typography>
                                    </Box>
                                ))
                            )}
                        </AccordionDetails>
                    </Accordion>

                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="historical-order-detail.files-accordion"
                        >
                            <Typography variant="h4">{translate('orders:FILES_AND_IMAGES')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <HistoricalOrderFiles
                                attachments={historicalOrder.attachments}
                                onDownload={handleDownload}
                            />
                        </AccordionDetails>
                    </Accordion>

                    {hasOtherDetails && (
                        <Accordion defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                data-test="historical-order-detail.other-details-accordion"
                            >
                                <Typography variant="h4">{translate('orders:HISTORICAL_OTHER_DETAILS')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {historicalOrder.comments && (
                                    <Box mb={2}>
                                        <Typography variant="caption" color="text.secondary">
                                            {translate('orders:COMMENTS')}
                                        </Typography>
                                        <Typography variant="body2">{historicalOrder.comments}</Typography>
                                    </Box>
                                )}
                                <Grid container spacing={2}>
                                    {historicalOrder.extraDetails.map((detail) => (
                                        <LabelledValue key={detail.label} label={detail.label} value={detail.value} />
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Grid>

                {/* The live page keeps lab and status information here; the archive's
                    equivalent is where the rows came from and when they arrived. */}
                <Grid item xs={12} md={3}>
                    <Paper variant="outlined" sx={{ padding: 2 }}>
                        <Typography variant="h4" gutterBottom>
                            {translate('orders:HISTORICAL_ARCHIVE')}
                        </Typography>
                        <Grid container spacing={2}>
                            <LabelledValue
                                md={12}
                                label={translate('orders:HISTORICAL_SOURCE_SYSTEM')}
                                value={historicalOrder.sourceSystem}
                            />
                            <LabelledValue
                                md={12}
                                label={translate('orders:HISTORICAL_IMPORTED_AT')}
                                value={
                                    historicalOrder.importedAt
                                        ? formatDate(new Date(historicalOrder.importedAt), 'date')
                                        : undefined
                                }
                            />
                            <LabelledValue
                                md={12}
                                label={translate('orders:HISTORICAL_IMPORT_BATCH')}
                                value={historicalOrder.importBatchId}
                            />
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default HistoricalOrderDetail;
